<template>
  <div>
    <div class="mb-3 d-flex">
      <strong class="d-block">
        Blokken
      </strong>

      <div class="ml-auto">
        <button class="btn-reset text-muted" type="button" @click="() => toggleCollapseAll(true)">
          Alles inklappen
        </button>

        <button class="btn-reset text-muted ml-3" type="button" @click="() => toggleCollapseAll(false)">
          Alles uitklappen
        </button>
      </div>
    </div>

    <div v-if="blocks.length">
      <block
        v-for="block in blocks"
        :key="block.id"
        :block="block"
        :is-collapsed="block.isCollapsed"
        @move="handleBlockMove"
        @remove="handleBlockRemove"
        @copy="handleBlockCopy"
        @collapse="handleBlockCollapse"
        @field-update="handleFieldUpdate"
      />
    </div>

    <div v-else class="blocks__empty-state p-3">
      Nog geen blokken, voeg hieronder je eerste blok toe.
    </div>

    <div class="blocks__bottom text-center py-3">
      <blocks-browser
        @add="handleBrowserAdd"
        :specification="specification"
      />
    </div>
  </div>
</template>

<script>
  import { arrayMoveImmutable } from 'array-move';
  import Block from './Block';
  import BlocksBrowser from './BlocksBrowser';
  import uuidv4 from '../../helpers/uuidv4';

  export default {
    props: ['props'],

    components: {
      Block,
      BlocksBrowser,
    },

    data() {
      return {
        specification: JSON.parse(this.props.blocksSpecification),
        blocks: [],
      };
    },

    created() {
      const outputElement = document.querySelector(this.props.blocksOutput);

      if (!outputElement) {
        throw new Error('Blocks element is missing output textarea');
      }

      try {
        this.blocks = JSON.parse(outputElement.value).map((block) => {
          block.specification = typeof this.specification[block.type] !== 'undefined'
            ? this.specification[block.type]
            : null;

          return block;
        });

        this.outputBlocksToHiddenInput();
      } catch (error) {
        window.toaster.show('Error bij laden van blokken', 3000, 'danger');
        console.error(error);
      }
    },

    methods: {
      /**
       * Handle adding blocks
       *
       * @param {object} specification
       * @return {void}
       */
      handleBrowserAdd(specification) {
        const fields = {};

        specification.fields.map((field) => {
          fields[field.name] = '';
        });

        this.blocks.push({
          id: this.randomId(),
          type: specification.type,
          specification,
          fields,
        });

        this.outputBlocksToHiddenInput();
      },

      /**
       * Handle moving blocks
       *
       * @param {int} id
       * @param {string} direction
       * @return {void}
       */
      handleBlockMove(id, direction) {
        const currentIndex = this.getBlockIndexById(id);

        const arr = Array.prototype.slice.call(this.blocks);

        // Somehow blocks are not always properly reloaded, so we force it
        // by setting an empty array
        this.blocks = [];

        // And then waiting for Vue to render the empty array before resetting
        // our data
        this.$nextTick(() => {
          if (currentIndex !== null && arr.length >= 1) {
            const newIndex = direction === 'up'
              ? currentIndex - 1
              : currentIndex + 1;

            this.blocks = arrayMoveImmutable(arr, currentIndex, newIndex);

            this.outputBlocksToHiddenInput();
          }
        });
      },

      /**
       * Handle the removal of a block
       *
       * @param {int} id
       * @return {void}
       */
      handleBlockRemove(id) {
        const currentIndex = this.getBlockIndexById(id);

        if (currentIndex !== null) {
          const arr = Array.prototype.slice.call(this.blocks);
          arr.splice(currentIndex, 1);
          this.blocks = arr;
        }

        this.outputBlocksToHiddenInput();
      },

      handleBlockCopy(id) {
        const blockIndex = this.getBlockIndexById(id);
        const block = this.getBlockById(id);

        const clonedBlock = {
          ...block,
          id: this.randomId(),
        };

        // Add cloned block to blocks array
        const arr = Array.prototype.slice.call(this.blocks);
        arr.splice(blockIndex, 0, clonedBlock);
        this.blocks = arr;

        this.outputBlocksToHiddenInput();
      },

      handleBlockCollapse(id) {
        this.blocks = this.blocks.map((block) => {
          if (block.id !== id) {
            return block;
          }

          if (typeof block.isCollapsed === 'undefined') {
            block.isCollapsed = false;
          }

          block.isCollapsed = !block.isCollapsed;

          return block;
        });
      },

      /**
       * Handle the update of a field in a block
       *
       * @param {int} blockId
       * @param {int} fieldId
       * @param {string} value
       * @return {void}
       */
      handleFieldUpdate([blockId, fieldId, value]) {
        this.blocks = Array.prototype.slice.call(this.blocks).map((block) => {
          if (block.id === blockId) {
            const fields = Object.assign({}, block.fields);
            fields[fieldId] = value;

            block.fields = fields;
          }

          return block;
        });

        this.outputBlocksToHiddenInput();
      },

      toggleCollapseAll(shouldCollapse) {
        this.blocks = this.blocks.map((block) => {
          block.isCollapsed = shouldCollapse;
          return block;
        });
      },

      /**
       * Output blocks to hidden input field
       *
       * @return {void}
       */
      outputBlocksToHiddenInput() {
        const outputElement = document.querySelector(this.props.blocksOutput);

        outputElement.value = JSON.stringify(this.blocks);
      },

      /**
       * Return the index of a block by the id
       *
       * @param {int} id
       * @return {int}
       */
      getBlockIndexById(id) {
        let index = null;

        this.blocks.forEach((item, itemIndex) => {
          if (item.id === id) {
            index = itemIndex;
          }
        });

        return index;
      },

      getBlockById(id) {
        let block = null;

        this.blocks.forEach((item) => {
          if (item.id === id) {
            block = item;
          }
        });

        return block;
      },

      randomId() {
        return uuidv4();
      },
    },
  };
</script>
